import { Link, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { usePersistStore, useStore } from "../../../store";
import { AdService } from "../../../services";
import { useCallback, useEffect, useState } from "react";
import { Utils } from "../../../utils";
import { PhoneText } from "../../../controls";
import {
  useOtherCategory,
  useCategory,
  useSubCategory,
  useMedium,
} from "../../../hooks/queries";

interface DetailRowProps {
  label: string;
  value?: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="previewRow">
    <span className="previewRow__label">{label}</span>
    <span className="previewRow__value">{value || "-"}</span>
  </div>
);

// adInProgress is persisted to localStorage so it can survive across app
// versions and lose fields when the schema changes. Validate before
// submit so the user gets a specific message instead of an opaque 500
// from the backend (e.g. "Can't extract geo keys" when location is
// missing).
function validateAdInProgress(ad: any): string | null {
  if (!ad) return "Listing data is missing. Please start again from Create.";
  if (!ad.images?.length) return "Please add at least one image.";
  if (!ad.title) return "Title is missing.";
  if (!ad.categoryId) return "Category is missing.";
  if (!ad.subCategoryId) return "Sub category is missing.";
  if (!ad.mediumId) return "Medium is missing.";
  if (!ad.height || !ad.width || !ad.depth)
    return "Dimensions (height, width, depth) are missing.";
  if (!ad.weight) return "Weight is missing.";
  if (!ad.price) return "Price is missing.";
  if (!ad.biddingStarts) return "Bid opening date is missing.";
  if (!ad.biddingEnds) return "Bid closing date is missing.";
  if (!ad.timezone) return "Timezone is missing.";
  if (!ad.description) return "Description is missing.";
  return null;
}

// Browser geolocation can be denied or unavailable, so coerce anything
// that isn't a finite [lng, lat] pair to [0, 0] before sending. The
// backend wraps this into a GeoJSON Point that the 2dsphere index can
// accept; without coercion, an invalid coordinate triggers "Can't
// extract geo keys" on insert.
function safeCoords(loc: any): number[] {
  if (
    Array.isArray(loc) &&
    loc.length === 2 &&
    Number.isFinite(loc[0]) &&
    Number.isFinite(loc[1])
  ) {
    return loc;
  }
  return [0, 0];
}

function Preview() {
  const navigate = useNavigate();
  const { user, token, adInProgress, setAdInProgress } = usePersistStore(
    (s: any) => s
  );
  const { setIsLoading, setErrorMessage, setSuccessMessage } = useStore(
    (s: any) => s
  );
  // Gate publishing on the seller accepting the selling terms — mirrors the
  // bid flow's accept-terms checkbox in PlaceYourBidModal.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  let categoryId: string = adInProgress?.categoryId || false;

  // Cached "Other" category bundle — shared across the listing forms.
  const { data: others = null, error: othersError } = useOtherCategory();
  useEffect(() => {
    if (othersError) setErrorMessage((othersError as Error).message);
  }, [othersError, setErrorMessage]);

  // Display-only reference lookups (cached). Sub-category/medium are token-gated.
  const { data: category = null } = useCategory(categoryId || undefined);
  const { data: subCategory = null } = useSubCategory(
    adInProgress?.subCategoryId,
    token
  );
  const { data: medium = null } = useMedium(adInProgress?.mediumId, token);

  const handleSubmit = useCallback(
    async (e: any) => {
      e?.preventDefault();
      if (!adInProgress) return;
      if (!acceptedTerms) return;

      const validationError = validateAdInProgress(adInProgress);
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }

      const payload = {
        ...adInProgress,
        location: safeCoords(adInProgress.location),
      };

      setIsLoading(true);
      let result = await AdService.create(token, payload);
      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        return;
      }

      setSuccessMessage("Listing is created successfully.");
      setIsLoading(false);
      setAdInProgress(null);
      navigate("/dashboard/listing");
    },
    [
      adInProgress,
      acceptedTerms,
      token,
      setErrorMessage,
      setIsLoading,
      setSuccessMessage,
      navigate,
      setAdInProgress,
    ]
  );

  if (!adInProgress) {
    return (
      <div className="bg-pink p-4">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-12 col-xl-9 mx-auto">
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-info-circle" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <h3 className="formSection__title">Nothing to preview</h3>
                    <p className="formSection__subtitle">
                      You don't have a listing in progress.
                    </p>
                  </div>
                </div>
                <Link
                  to="/dashboard/create"
                  className="placeBidModal__submit"
                  style={{ textDecoration: "none", display: "inline-flex" }}
                >
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                  <span>Create new listing</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sizeText =
    adInProgress?.height || adInProgress?.width || adInProgress?.depth
      ? `${adInProgress?.height || 0}″ × ${adInProgress?.width || 0}″ × ${
          adInProgress?.depth || 0
        }″`
      : "-";

  // Render the shipping address in the standard 3-line postal layout:
  //   <street>            (address1)
  //   <unit>              (address2, omitted when empty)
  //   <city>, <state> <zip>
  // City/state/zip is one line with a comma between city and state and a
  // space (not a comma) before the zip.
  const cityStateZip = [
    [user?.city, user?.state].filter(Boolean).join(", "),
    user?.zip,
  ]
    .filter(Boolean)
    .join(" ");
  const addressLines = [user?.address1, user?.address2, cityStateZip].filter(
    Boolean
  );
  const addressNode =
    addressLines.length > 0 ? (
      <span style={{ display: "block" }}>
        {addressLines.map((line: string, i: number) => (
          <span key={i} style={{ display: "block" }}>
            {line}
          </span>
        ))}
      </span>
    ) : null;

  return (
    <div className="bg-pink p-4 createListingMain">
      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-12 col-xl-9 mx-auto">
            {/* Page header */}
            <div className="tableCardHeader px-0 mb-4">
              <div className="tableCardHeader__titleBlock">
                <h2 className="tableCardHeader__title m-0">Preview details</h2>
                <p className="tableCardHeader__subtitle m-0">
                  Review everything below — once you submit, your draft goes for
                  approval.
                </p>
              </div>
            </div>

            {/* Photos */}
            {adInProgress?.images?.length > 0 && (
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-images" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <h3 className="formSection__title">Photos</h3>
                    <p className="formSection__subtitle">
                      {adInProgress.images.length}{" "}
                      {adInProgress.images.length === 1 ? "image" : "images"}
                    </p>
                  </div>
                </div>
                <div className="uploadGrid">
                  {adInProgress.images.map((src: string, idx: number) => (
                    <div key={idx} className="uploadGrid__item">
                      <img
                        src={src}
                        alt={`Preview ${idx + 1}`}
                        className="uploadGrid__img"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Artwork details */}
            <div className="formSection">
              <div className="formSection__header">
                <div className="formSection__iconWrap">
                  <i className="bi bi-palette" aria-hidden="true" />
                </div>
                <div className="formSection__titleBlock">
                  <h3 className="formSection__title">Artwork details</h3>
                  <p className="formSection__subtitle">
                    What buyers will see on the listing.
                  </p>
                </div>
                <button
                  type="button"
                  className="previewEditBtn"
                  onClick={() => navigate("/dashboard/update-preview")}
                  aria-label="Edit artwork details"
                >
                  <i className="bi bi-pencil" aria-hidden="true" />
                  <span>Edit</span>
                </button>
              </div>

              <DetailRow label="Title" value={adInProgress?.title} />
              <DetailRow
                label="Category"
                value={
                  category?._id === others?.category?._id &&
                  adInProgress?.otherCategoryName
                    ? `${category?.name}: ${adInProgress?.otherCategoryName}`
                    : category?.name
                }
              />
              <DetailRow
                label="Sub category"
                value={
                  subCategory?._id === others?.subCategory?._id &&
                  adInProgress?.otherSubCategoryName
                    ? `${subCategory?.name}: ${adInProgress?.otherSubCategoryName}`
                    : subCategory?.name
                }
              />
              <DetailRow
                label="Medium"
                value={
                  medium?._id === others?.medium?._id &&
                  adInProgress?.otherMediumName
                    ? `${medium?.name}: ${adInProgress?.otherMediumName}`
                    : medium?.name
                }
              />
              <DetailRow label="Dimensions" value={sizeText} />
              <DetailRow
                label="Weight"
                value={
                  adInProgress?.weight
                    ? `${adInProgress.weight} lbs`
                    : undefined
                }
              />
              <DetailRow
                label="Opening bid"
                value={Utils.formatToCurrency(adInProgress?.price)}
              />
              <DetailRow label="Timezone" value={adInProgress?.timezone} />
              <DetailRow
                label="Bid opens on"
                value={Utils.formatDateTime(adInProgress?.biddingStarts)}
              />
              <DetailRow
                label="Bid ends on"
                value={Utils.formatDateTime(adInProgress?.biddingEnds)}
              />
              {adInProgress?.description && (
                <div className="previewDescription">
                  <span className="previewRow__label">Description</span>
                  <p className="previewDescription__text">
                    {adInProgress.description}
                  </p>
                </div>
              )}
            </div>

            {/* Personal details */}
            <div className="formSection">
              <div className="formSection__header">
                <div className="formSection__iconWrap">
                  <i className="bi bi-person-badge" aria-hidden="true" />
                </div>
                <div className="formSection__titleBlock">
                  <h3 className="formSection__title">Personal details</h3>
                  <p className="formSection__subtitle">
                    Used for buyer trust and shipping.
                  </p>
                </div>
                <button
                  type="button"
                  className="previewEditBtn"
                  onClick={() => navigate("/dashboard/profile")}
                  aria-label="Edit personal details"
                >
                  <i className="bi bi-pencil" aria-hidden="true" />
                  <span>Edit</span>
                </button>
              </div>

              <DetailRow label="Full name" value={user?.name} />
              <DetailRow
                label="Date of birth"
                value={Utils.formatDate(user?.dob)}
              />
              <DetailRow
                label="Phone number"
                value={user?.phone ? <PhoneText text={user.phone} /> : null}
              />
              <DetailRow label="Email" value={user?.email} />
              <DetailRow label="Address" value={addressNode || undefined} />
            </div>

            {/* Sticky action bar — the accept-terms checkbox lives inside it
                (as a stacked top row) so the pinned bar can't overlap it. */}
            <div className="formStickyBar formStickyBar--stacked">
              <Form.Check
                type="checkbox"
                id="previewAcceptSellingTerms"
                className="termsCheck formStickyBar__terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                label={
                  <>
                    I have read and agree to the{" "}
                    <Link
                      to="/selling-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Selling Terms &amp; Conditions
                    </Link>
                  </>
                }
              />
              <div className="formStickyBar__row">
                <span className="formStickyBar__msg">
                  Ready to publish? You can edit later from your listings.
                </span>
                <div className="formStickyBar__actions">
                  <button
                    type="button"
                    className="placeBidModal__cancel"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="placeBidModal__submit"
                    onClick={handleSubmit}
                    disabled={!acceptedTerms}
                  >
                    <i className="bi bi-check2-circle" aria-hidden="true" />
                    <span>Submit listing</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Preview;
